<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <div class="page-head">
              <h2>Faculty — IABF {{ adminFestivalYear }}</h2>
              <div class="head-actions">
                <v-btn
                  small
                  outlined
                  :disabled="!facultyEmails.length"
                  @click="copyFacultyEmails"
                >
                  {{ emailsCopyNote || 'Copy faculty emails' }}
                </v-btn>
                <v-btn small color="primary" dark to="/iabf/admin/faculty/new">+ Invite faculty</v-btn>
              </div>
            </div>
            <p class="muted">
              Teachers for this festival. They set a password at dashboard.iabf.dance with this email —
              then dietary, intro, flights, free carpool, and one complimentary meal guest.
              Room is assigned here only (counts against inventory). They don’t see it on their profile.
              Arrive / leave dates drive which meals they get on Meals &amp; guests.
              “IABF yrs” (including this year) is what shows on their community intro.
            </p>
            <p v-if="loading" class="muted">Loading…</p>
            <div v-else class="table-scroll">
            <table class="faculty-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th title="Including this year — shows on their community intro">IABF yrs</th>
                  <th>Room</th>
                  <th>Room #</th>
                  <th>Arrive</th>
                  <th>Leave</th>
                  <th class="num">Pay</th>
                  <th class="num">Travel reimb.</th>
                  <th class="num">Other reimb.</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in rows" :key="f.id">
                  <td>
                    <strong>{{ f.firstName }} {{ f.lastName }}</strong>
                    <div v-if="f.phone" class="muted small">{{ f.phone }}</div>
                  </td>
                  <td>{{ f.email }}</td>
                  <td>
                    <input
                      class="years-input"
                      type="number"
                      min="1"
                      max="40"
                      :value="f.yearsAtIabf || ''"
                      :disabled="savingId === f.id"
                      placeholder="1"
                      @change="onYearsAtIabfChange(f, $event)"
                    >
                  </td>
                  <td class="room-cell">
                    <v-select
                      :value="f.room || ''"
                      :items="roomItems"
                      item-text="text"
                      item-value="value"
                      hide-details
                      dense
                      :disabled="savingId === f.id"
                      @change="onRoomChange(f, $event)"
                    />
                  </td>
                  <td>
                    <input
                      v-if="isOnsiteRoom(f.room)"
                      class="room-number-input"
                      type="text"
                      :value="f.roomNumber || ''"
                      :disabled="savingId === f.id"
                      placeholder="e.g. 12"
                      @change="onRoomNumberChange(f, $event)"
                    >
                    <span v-else class="muted small">—</span>
                  </td>
                  <td>
                    <input
                      class="date-input"
                      type="date"
                      :value="f.arrivalDate || defaultArrival"
                      :disabled="savingId === f.id"
                      @change="onStayChange(f, 'arrivalDate', $event)"
                    >
                  </td>
                  <td>
                    <input
                      class="date-input"
                      type="date"
                      :value="f.departureDate || defaultDeparture"
                      :disabled="savingId === f.id"
                      @change="onStayChange(f, 'departureDate', $event)"
                    >
                  </td>
                  <td class="num">
                    <input
                      class="money-input"
                      type="number"
                      min="0"
                      step="0.01"
                      :value="moneyDisplay(f.honorariumUsd)"
                      :disabled="savingId === f.id"
                      @change="onMoneyChange(f, 'honorariumUsd', $event)"
                    >
                  </td>
                  <td class="num">
                    <input
                      class="money-input"
                      type="number"
                      min="0"
                      step="0.01"
                      :value="moneyDisplay(f.travelReimbursedUsd)"
                      :disabled="savingId === f.id"
                      @change="onMoneyChange(f, 'travelReimbursedUsd', $event)"
                    >
                  </td>
                  <td class="num">
                    <input
                      class="money-input"
                      type="number"
                      min="0"
                      step="0.01"
                      :value="moneyDisplay(f.otherReimbursedUsd)"
                      :disabled="savingId === f.id"
                      @change="onMoneyChange(f, 'otherReimbursedUsd', $event)"
                    >
                  </td>
                  <td>
                    <input
                      class="notes-input"
                      type="text"
                      :value="f.payNotes || ''"
                      :disabled="savingId === f.id"
                      placeholder="e.g. flights, mileage"
                      @change="onNotesChange(f, $event)"
                    >
                  </td>
                  <td class="actions">
                    <button type="button" class="link-btn" @click="masqueradeAs(f)">View as</button>
                    <button type="button" class="link-btn danger" @click="onDelete(f)">Remove</button>
                  </td>
                </tr>
                <tr v-if="rows.length" class="totals-row">
                  <td colspan="7"><strong>Totals</strong></td>
                  <td class="num"><strong>${{ fmtMoney(totals.honorarium) }}</strong></td>
                  <td class="num"><strong>${{ fmtMoney(totals.travel) }}</strong></td>
                  <td class="num"><strong>${{ fmtMoney(totals.other) }}</strong></td>
                  <td colspan="2" class="muted">All-in ${{ fmtMoney(totals.all) }}</td>
                </tr>
                <tr v-if="!rows.length">
                  <td colspan="12" class="muted">No faculty invited yet.</td>
                </tr>
              </tbody>
            </table>
            </div>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { FACULTY_ROOM_ITEMS, facultyMoneyNum, facultyYearsAtIabfNum } from '@/utils/iabf-faculty'
import { uniqueEmails } from '@/utils/iabf-people'
import { isOnsiteForRooming, normalizeRoomNumber } from '@/utils/iabf-registration-package'
import { festivalWeek } from '@/config/iabf-festival-dates'

export default {
  name: 'IABFFacultyAdmin',
  data () {
    return {
      loading: true,
      rows: [],
      savingId: '',
      emailsCopyNote: '',
      roomItems: FACULTY_ROOM_ITEMS
    }
  },
  computed: {
    adminFestivalYear () {
      return this.$store.getters.adminFestivalYear
    },
    totals () {
      const honorarium = this.rows.reduce((s, f) => s + facultyMoneyNum(f.honorariumUsd), 0)
      const travel = this.rows.reduce((s, f) => s + facultyMoneyNum(f.travelReimbursedUsd), 0)
      const other = this.rows.reduce((s, f) => s + facultyMoneyNum(f.otherReimbursedUsd), 0)
      return { honorarium, travel, other, all: honorarium + travel + other }
    },
    facultyEmails () {
      return uniqueEmails(this.rows.map(f => f.email))
    },
    defaultArrival () {
      return festivalWeek(this.adminFestivalYear).arrivalIso
    },
    defaultDeparture () {
      return festivalWeek(this.adminFestivalYear).departureIso
    }
  },
  watch: {
    adminFestivalYear () {
      this.load()
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        this.rows = await this.$store.dispatch('loadIabfFaculty')
      } catch (e) {
        console.error(e)
        this.rows = []
      }
      this.loading = false
    },
    moneyDisplay (n) {
      const v = facultyMoneyNum(n)
      return v ? String(v) : ''
    },
    fmtMoney (n) {
      return facultyMoneyNum(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },
    async onMoneyChange (f, field, ev) {
      const next = facultyMoneyNum(ev && ev.target && ev.target.value)
      if (facultyMoneyNum(f[field]) === next) {
        return
      }
      this.savingId = f.id
      try {
        const patch = { id: f.id }
        patch[field] = next
        await this.$store.dispatch('updateIabfFaculty', patch)
        this.$set(f, field, next)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Could not save: ' + (e && e.message ? e.message : e))
      }
      this.savingId = ''
    },
    async onNotesChange (f, ev) {
      const next = String((ev && ev.target && ev.target.value) || '').trim()
      if (String(f.payNotes || '') === next) {
        return
      }
      this.savingId = f.id
      try {
        await this.$store.dispatch('updateIabfFaculty', { id: f.id, payNotes: next })
        this.$set(f, 'payNotes', next)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Could not save notes: ' + (e && e.message ? e.message : e))
      }
      this.savingId = ''
    },
    isOnsiteRoom (room) {
      return isOnsiteForRooming(room)
    },
    async onRoomChange (f, room) {
      const next = room == null ? '' : room
      if ((f.room || '') === next) {
        return
      }
      this.savingId = f.id
      try {
        await this.$store.dispatch('updateIabfFaculty', { id: f.id, room: next })
        this.$set(f, 'room', next)
        if (!isOnsiteForRooming(next)) {
          this.$set(f, 'roomNumber', '')
        }
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Could not update room: ' + (e && e.message ? e.message : e))
      }
      this.savingId = ''
    },
    async onStayChange (f, field, ev) {
      const next = String((ev && ev.target && ev.target.value) || '').trim()
      const prev = String(f[field] || '').trim()
      if (prev === next) {
        return
      }
      this.savingId = f.id
      try {
        const patch = { id: f.id }
        patch[field] = next
        await this.$store.dispatch('updateIabfFaculty', patch)
        this.$set(f, field, next)
        const arrival = String(f.arrivalDate || this.defaultArrival)
        const departure = String(f.departureDate || this.defaultDeparture)
        if (departure < arrival) {
          await this.$store.dispatch('updateIabfFaculty', { id: f.id, departureDate: arrival })
          this.$set(f, 'departureDate', arrival)
        }
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Could not save dates: ' + (e && e.message ? e.message : e))
        if (ev && ev.target) {
          ev.target.value = f[field] || (field === 'arrivalDate' ? this.defaultArrival : this.defaultDeparture)
        }
      }
      this.savingId = ''
    },
    async onYearsAtIabfChange (f, ev) {
      const next = facultyYearsAtIabfNum(ev && ev.target && ev.target.value)
      if (facultyYearsAtIabfNum(f.yearsAtIabf) === next) {
        return
      }
      this.savingId = f.id
      try {
        await this.$store.dispatch('updateIabfFaculty', { id: f.id, yearsAtIabf: next })
        this.$set(f, 'yearsAtIabf', next)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Could not save years at IABF: ' + (e && e.message ? e.message : e))
        if (ev && ev.target) {
          ev.target.value = f.yearsAtIabf || ''
        }
      }
      this.savingId = ''
    },
    async onRoomNumberChange (f, ev) {
      const next = normalizeRoomNumber(ev && ev.target && ev.target.value)
      if (normalizeRoomNumber(f.roomNumber) === next) {
        return
      }
      this.savingId = f.id
      try {
        await this.$store.dispatch('updateIabfFaculty', { id: f.id, roomNumber: next })
        this.$set(f, 'roomNumber', next)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Could not save room number: ' + (e && e.message ? e.message : e))
        if (ev && ev.target) {
          ev.target.value = f.roomNumber || ''
        }
      }
      this.savingId = ''
    },
    masqueradeAs (f) {
      this.$store.commit('setMasqueradeDancer', f)
      this.$store.dispatch('refreshEffectiveDancer').catch(() => {})
      this.$router.push('/')
    },
    async copyFacultyEmails () {
      const emails = this.facultyEmails
      const text = emails.join('\n')
      if (!text) {
        return
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.emailsCopyNote = `✓ Copied ${emails.length}`
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt(`Copy these ${emails.length} email(s):`, text)
        this.emailsCopyNote = 'Copy failed — pasted in prompt'
      }
      setTimeout(() => { this.emailsCopyNote = '' }, 2400)
    },
    async onDelete (f) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Remove ' + f.firstName + ' ' + f.lastName + ' from faculty for this year?')) {
        return
      }
      try {
        await this.$store.dispatch('deleteIabfFaculty', f.id)
        this.rows = this.rows.filter(r => r.id !== f.id)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Remove failed: ' + (e && e.message ? e.message : e))
      }
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.head-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.muted { color: #888; }
.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 16px;
}
.faculty-table { width: 100%; min-width: 1120px; border-collapse: collapse; }
.faculty-table th, .faculty-table td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #eee; }
.actions { white-space: nowrap; }
.room-cell { min-width: 200px; }
.num { text-align: right; }
.small { font-size: 12px; }
.room-number-input,
.years-input,
.date-input {
  width: 88px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font: inherit;
}
.years-input { width: 64px; }
.date-input { width: 138px; }
.money-input, .notes-input {
  width: 100%;
  min-width: 88px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font: inherit;
}
.notes-input { min-width: 140px; }
.totals-row td { border-top: 2px solid #ccc; padding-top: 12px; }
.link-btn { background: none; border: none; color: #267E7D; cursor: pointer; text-decoration: underline; font: inherit; margin-right: 10px; }
.link-btn.danger { color: #c0392b; }
</style>
